// Public invite viewer used inside the SPA. In production the SSR Cloud Function
// serves the first paint + OG tags; this component renders the live, interactive
// brochure (hydration target). It can also fetch directly when reached via SPA nav.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getEvent } from "../../firebase/db.js";
import { resolvePageUrls, urlFor } from "../../firebase/storage.js";
import { isConfigured } from "../../firebase/init.js";
import Brochure from "./Brochure.jsx";

// SSR inlines the event as window.__EVENT__ with raw imagePages but no resolved
// imagePageUrls. Resolve them here so planPages() can render every page on the
// hydration path (otherwise an images-source invite shows only the SSR cover).
function hydrateEvent(e) {
  if (!e) return null;
  if (e.source === "images" && e.imagePages?.length && !e.imagePageUrls?.length) {
    return { ...e, imagePageUrls: e.imagePages.map(urlFor) };
  }
  return e;
}

export default function InviteViewer() {
  const { slug } = useParams();
  const [event, setEvent] = useState(
    // SSR can inline the event as window.__EVENT__ for instant hydration.
    typeof window !== "undefined" ? hydrateEvent(window.__EVENT__) : null
  );
  const [status, setStatus] = useState(event ? "ready" : "loading");
  const [showClaimPrompt, setShowClaimPrompt] = useState(false);
  const [viewMode, setViewMode] = useState("mobile");
  const [isCompactViewport, setIsCompactViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 900 : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    function onResize() {
      setIsCompactViewport(window.innerWidth <= 900);
    }
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (event || !isConfigured) {
      if (!event && !isConfigured) setStatus("notfound");
      return;
    }
    (async () => {
      try {
        const search = typeof window !== "undefined" ? window.location.search : "";
        const e = await getEvent(slug, search);
        if (!e || e.status !== "published") { setStatus("notfound"); return; }
        if (e.source === "images" && e.imagePages?.length) {
          e.imagePageUrls = await resolvePageUrls(e.imagePages);
        }
        setEvent(e);
        setStatus("ready");
      } catch {
        setStatus("notfound");
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!event?.details?.promoDemo) {
      setShowClaimPrompt(false);
      return;
    }
    const timer = window.setTimeout(() => setShowClaimPrompt(true), 1800);
    return () => window.clearTimeout(timer);
  }, [event]);

  if (status === "loading") {
    return <div className="container center" style={{ paddingTop: 120 }}><p className="muted">Loading invite…</p></div>;
  }
  if (status === "notfound") {
    return (
      <div className="container center stack" style={{ paddingTop: 120, maxWidth: 480 }}>
        <h1>Invite not found</h1>
        <p className="muted">No published invite at einvite.to/{slug}.</p>
        <a className="btn" href="/">Create your own</a>
      </div>
    );
  }

  if (isCompactViewport) {
    return (
      <>
        <Brochure event={event} />
        {showClaimPrompt && (
          <button
            className="claim-prompt"
            type="button"
            onClick={() => { window.location.href = "/"; }}
          >
            <span>einvite.to/{event.details.promoClaimSlug || slug} is available</span>
            <strong>Claim this link</strong>
          </button>
        )}
      </>
    );
  }

  return (
    <div className={`public-invite-shell public-invite-mode-${viewMode}`}>
      <div className="public-invite-toolbar" aria-label="Invite view mode">
        <button
          type="button"
          className={viewMode === "mobile" ? "btn mini" : "btn btn-ghost mini"}
          onClick={() => setViewMode("mobile")}
        >
          Mobile
        </button>
        <button
          type="button"
          className={viewMode === "desktop" ? "btn mini" : "btn btn-ghost mini"}
          onClick={() => setViewMode("desktop")}
        >
          Desktop
        </button>
      </div>
      <div className={`public-invite-stage public-invite-stage-${viewMode}`}>
        <div className="public-invite-frame">
          <Brochure event={event} style={{ height: "100%" }} />
        </div>
      </div>
      {showClaimPrompt && (
        <button
          className="claim-prompt"
          type="button"
          onClick={() => { window.location.href = "/"; }}
        >
          <span>einvite.to/{event.details.promoClaimSlug || slug} is available</span>
          <strong>Claim this link</strong>
        </button>
      )}
    </div>
  );
}
